/**
 * "How to play" (docs/ux.md §15a.1): the topics in phase order, the rule IDs each one rests on,
 * and the numbers its text quotes, read from the game's ruleset (U-LEARN-03).
 */
import type { Ruleset } from '@brig/rules-engine';

export const TOPICS = [
  'goal',
  'turn',
  'muster',
  'wind',
  'movement',
  'storm',
  'fire',
  'ports',
  'forts',
  'royal',
  'end',
] as const;

export type Topic = (typeof TOPICS)[number];

export function isTopic(value: unknown): value is Topic {
  return typeof value === 'string' && (TOPICS as readonly string[]).includes(value);
}

/** The rules a page explains, for the curious: small and last on the page. */
export const TOPIC_RULES: Readonly<Record<Topic, readonly string[]>> = {
  goal: ['R-END-01', 'R-END-02'],
  turn: ['R-TURN-02', 'R-TURN-07'],
  muster: ['R-TURN-03', 'R-TURN-05'],
  wind: ['R-SPIN-01', 'R-MOVE-02', 'R-MOVE-03'],
  movement: ['R-MOVE-04', 'R-MOVE-05', 'R-MOVE-08'],
  storm: ['R-STORM-01', 'R-STORM-03', 'R-STORM-04'],
  fire: ['R-FIRE-01', 'R-FIRE-02', 'R-VOLLEY-01', 'R-VOLLEY-03'],
  ports: ['R-PORT-03', 'R-PORT-05', 'R-SREP-01', 'R-SREP-03'],
  forts: ['R-FIRE-07', 'R-FORT-01', 'R-FREP-01', 'R-FREP-04'],
  royal: ['R-ROYAL-02', 'R-ROYAL-03', 'R-ROYAL-04'],
  end: ['R-END-01', 'R-END-02', 'R-END-03', 'R-END-04'],
};

/** Everything a page's text may interpolate; every page gets all of them. */
export function topicNumbers(ruleset: Ruleset) {
  const first = ruleset.fortClasses.first_line;
  const second = ruleset.fortClasses.second_line;
  // The wind page's brig (R-MOVE-02/03): its reach straight downwind and dead upwind.
  const brig = ruleset.shipClasses.brig;
  const upwind = Math.max(0, brig.freshSpeed - 4);
  return {
    brigFast: brig.freshSpeed,
    brigSlow: ruleset.oarsAndSteamUseBest ? Math.max(upwind, brig.calmSpeed) : upwind,
    hitPercent: Math.round(ruleset.hitChance * 100),
    volleyMin: ruleset.volleyMinShooters,
    portLimit: ruleset.portShipLimit,
    crew: ruleset.fortRepairCrew,
    fortRepair: ruleset.fortRepairPerTurn,
    hold: ruleset.royalPortHoldTurns,
    fort1Shots: first.shotsPerTurn,
    fort1Range: first.range,
    fort1Hits: first.destroyedAt,
    fort2Shots: second.shotsPerTurn,
    fort2Range: second.range,
    fort2Hits: second.destroyedAt,
  };
}
